/**
 * The canonical "Create New" catalog.
 *
 * Extracted from `create-actions-menu.tsx` so the quick-create dropdown AND the
 * command palette drive off ONE list with ONE set of gates. Two hand-maintained
 * copies would drift, and a create action that exists in one surface but not the
 * other is exactly the kind of inconsistency this is meant to prevent.
 */

import {
  Building2,
  UserCheck,
  MessageSquare,
  Clock,
  Ticket,
  FileText,
  Calendar,
  CheckSquare,
  Mail,
  TrendingUp,
  Package,
  type LucideIcon,
} from "lucide-react";
import { MODAL_IDS } from "@wtc/ui";
import type { FeatureFlags } from "@/lib/contexts/feature-flags-context";

// The gating inputs each create item is filtered against. Passed to every
// item's `gate` so the visible set is a pure filter over CREATE_ACTIONS.
export interface CreateActionGateContext {
  canAccessCRM: boolean;
  isFeatureEnabled: (flagName: keyof FeatureFlags) => boolean;
  isWTCEmployee: boolean;
  contractCreationEnabled: boolean;
}

export interface CreateAction {
  key: string;
  icon: LucideIcon;
  iconClassName: string;
  label: string;
  desc: string;
  gate: (ctx: CreateActionGateContext) => boolean;
  action:
    | { type: "modal"; modalId: string }
    | { type: "route"; href: string }
    | { type: "link"; href: string };
}

// The single client-allowed create item; reused in the clientRestricted branch
// so its enabled path stays byte-identical to the full menu's ticket item.
export const SUPPORT_TICKET_ACTION: CreateAction = {
  key: "ticket",
  icon: Ticket,
  iconClassName: "mr-3 h-4 w-4 text-blue-500",
  label: "Support Ticket",
  desc: "Create a new ticket",
  gate: (ctx) => ctx.isFeatureEnabled("canCreateTickets"),
  action: { type: "route", href: "/tickets/create" },
};

// The full "Create New" menu, in render order. Data-drives what used to be
// eleven-plus near-identical <DropdownMenuItem> blocks. Order, gating, icons,
// copy, and click targets match the previous hand-written markup exactly.
export const CREATE_ACTIONS: CreateAction[] = [
  {
    key: "email",
    icon: Mail,
    iconClassName: "mr-3 h-4 w-4 text-red-500",
    label: "Send Email",
    desc: "Compose new email",
    gate: (ctx) => ctx.canAccessCRM,
    action: { type: "modal", modalId: MODAL_IDS.messages.compose },
  },
  {
    key: "communication",
    icon: MessageSquare,
    iconClassName: "mr-3 h-4 w-4 text-teal-600",
    label: "Communication",
    desc: "Log a new communication",
    gate: (ctx) => ctx.canAccessCRM,
    action: { type: "modal", modalId: MODAL_IDS.communications.create },
  },
  SUPPORT_TICKET_ACTION,
  {
    key: "task",
    icon: CheckSquare,
    iconClassName: "mr-3 h-4 w-4 text-blue-600",
    label: "Task",
    desc: "Create new task",
    gate: (ctx) => ctx.isFeatureEnabled("canCreateTasks"),
    action: { type: "modal", modalId: MODAL_IDS.tasks.create },
  },
  {
    key: "contact",
    icon: UserCheck,
    iconClassName: "mr-3 h-4 w-4 text-green-500",
    label: "Contact",
    desc: "Add new contact",
    gate: (ctx) => ctx.canAccessCRM,
    action: { type: "modal", modalId: MODAL_IDS.contacts.createQuick },
  },
  {
    key: "company",
    icon: Building2,
    iconClassName: "mr-3 h-4 w-4 text-cyan-600",
    label: "Company",
    desc: "Add new company",
    gate: (ctx) => ctx.canAccessCRM,
    action: { type: "modal", modalId: MODAL_IDS.companies.create },
  },
  {
    key: "deal",
    icon: TrendingUp,
    iconClassName: "mr-3 h-4 w-4 text-purple-500",
    label: "Deal",
    desc: "Create new deal",
    gate: (ctx) => ctx.canAccessCRM,
    action: { type: "modal", modalId: MODAL_IDS.deals.create },
  },
  {
    key: "quote",
    icon: FileText,
    iconClassName: "mr-3 h-4 w-4 text-purple-600",
    label: "Request a Quote",
    desc: "Ask the tech team to quote a deal",
    gate: (ctx) => ctx.canAccessCRM,
    action: { type: "modal", modalId: MODAL_IDS.deals.requestQuote },
  },
  {
    key: "offering",
    icon: Package,
    iconClassName: "mr-3 h-4 w-4 text-amber-500",
    label: "Offering",
    desc: "Create new service offering",
    gate: (ctx) => ctx.canAccessCRM,
    action: { type: "modal", modalId: MODAL_IDS.offerings.create },
  },
  {
    // Contract creation is temporarily disabled until its feature flag is added;
    // it renders as an <a> (Link) rather than an onClick item, so it keeps the
    // `link` action type.
    key: "contract",
    icon: FileText,
    iconClassName: "mr-3 h-4 w-4 text-purple-500",
    label: "Contract",
    desc: "Generate contract",
    gate: (ctx) => ctx.contractCreationEnabled,
    action: { type: "link", href: "/contracts" },
  },
  {
    key: "time-entry",
    icon: Clock,
    iconClassName: "mr-3 h-4 w-4 text-orange-500",
    label: "Time Entry",
    desc: "Log time worked",
    gate: (ctx) => ctx.isFeatureEnabled("canCreateTimeEntries"),
    action: { type: "modal", modalId: MODAL_IDS.timeEntries.createQuick },
  },
  {
    // Meeting/Event — hidden for WTC Employees.
    key: "meeting",
    icon: Calendar,
    iconClassName: "mr-3 h-4 w-4 text-indigo-500",
    label: "Meeting",
    desc: "Schedule meeting",
    gate: (ctx) => !ctx.isWTCEmployee,
    action: { type: "modal", modalId: MODAL_IDS.meetings.create },
  },
];
